let style = document.createElement("style");
style.innerHTML = `
ob-disclosure-nav * ul,
.ob-disclosure-nav__list {
  background-color: var(--disclosure-nav-bg, #fafafa);
  box-shadow: 0 6px 12px rgba(0,0,0,0.175);

  display: block;
  list-style-type: none;

  margin: 0;
  max-height: 90vh;
  min-width: 12.5rem;
  overflow-y: auto;
  padding: 0.5em 0;

  position: absolute;
  z-index: 1;
}

ob-disclosure-nav ul a,
.ob-disclosure-nav__link {
  border: 0;
  color: var(--disclosure-nav-item-color, #666);
  display: block;
  margin: 0;
  padding: 1.25em 1em;
  text-decoration: none;
}

ob-disclosure-nav ul a:focus,
.ob-disclosure-nav__link:focus,
ob-disclosure-nav ul a:hover,
.ob-disclosure-nav__link:hover {
  background-color: var(--brand-secondary, #666);
  color: var(--brand-secondary-contrast, white);
}

ob-disclosure-nav ul a:focus,
.ob-disclosure-nav__link:focus {
  position: relative;
  outline: 0.125rem solid rgba(0, 0, 0, 0.5);
}

ob-disclosure-nav button,
.ob-disclosure-nav__button {
  appearance: none;
  background-color: transparent;
  border: none;
  color: inherit;
}

ob-disclosure-nav button::after,
.ob-disclosure-nav__button::after {
  display: inline-block;
  content: "";
  border-bottom: 0.125rem solid white;
  border-right: 0.125rem solid white;
  height: 0.5em;
  margin-left: 0.75em;
  width: 0.5em;
  transform: rotate(45deg);
  transform-origin: center;
  transition: transform 200ms ease-out;
}

ob-disclosure-nav button[aria-expanded=true]::after,
.ob-disclosure-nav__button[aria-expanded=true]::after {
  transform: rotate(-135deg);
}

ob-disclosure-nav button:hover,
.ob-disclosure-nav__button:hover,
ob-disclosure-nav button[aria-expanded=true],
.ob-disclosure-nav__button[aria-expanded=true] {
  background-color: var(--disclosure-item-hover-bg, #005a9c);
  color: var(--disclosure-item-hover-color, #fff);
}

ob-disclosure-nav button:hover::after,
.ob-disclosure-nav__button:hover::after,
ob-disclosure-nav button[aria-expanded=true]::after,
.ob-disclosure-nav__button[aria-expanded=true]::after {
  border-color: #fff;
  color: var(--disclosure-item-hover-color, #fff);
}
`;

document.head.appendChild(style);

export default class DisclosureNav extends HTMLElement {
  constructor() {
    super();
    this.triggerNodes = [];
    this.controlledNodes = [];
    this.openIndex = null;
    this.useArrowKeys = true;

    let buttons = this.querySelectorAll("button[aria-expanded][aria-controls]");

    for (let button of buttons) {
      let menu = button.parentNode.querySelector(
        ".ob-disclosure-nav__list, ul"
      );
      if (menu) {
        // save ref to button and controlled menu
        this.triggerNodes.push(button);
        this.controlledNodes.push(menu);

        // collapse menus
        button.setAttribute("aria-expanded", "false");
        this.toggleMenu(menu, false);

        // attach event listeners
        menu.addEventListener("keydown", this.handleMenuKeyDown.bind(this));
        button.addEventListener("click", this.handleButtonClick.bind(this));
        button.addEventListener("keydown", this.handleButtonKeyDown.bind(this));
      }
    }

    this.addEventListener("focusout", this.handleBlur.bind(this));
  }

  toggleMenu(domNode, show) {
    if (domNode) {
      domNode.style.display = show ? "block" : "none";
      if (show) {
        let clientRect = domNode.getBoundingClientRect();
        let rootClientRect = this.getBoundingClientRect();
        let clientX2 = clientRect.x + clientRect.width;
        let rootX2 = rootClientRect.x + rootClientRect.width;
        if (clientX2 > rootX2) {
          domNode.style.left = rootX2 - clientX2 + "px";
        }
        domNode.scrollIntoView({ block: "nearest", behavior: "smooth" });
      } else {
        domNode.style.left = null;
      }
    }
  }

  toggleExpand(index, expanded) {
    // close open menu, if applicable
    if (this.openIndex !== index) {
      this.toggleExpand(this.openIndex, false);
    }

    // handle menu at called index
    if (this.triggerNodes[index]) {
      this.openIndex = expanded ? index : null;
      this.triggerNodes[index].setAttribute("aria-expanded", expanded);
      this.toggleMenu(this.controlledNodes[index], expanded);
    }
  }

  controlFocusByKey(event, nodeList, currentIndex) {
    let handledKeys = [
      "ArrowLeft",
      "ArrowUp",
      "ArrowRight",
      "ArrowDown",
      "Home",
      "End",
    ];

    if (handledKeys.includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
    }

    switch (event.key) {
      case "ArrowLeft":
        if (this.triggerNodes.length > 0) {
          let index =
            this.openIndex <= 0
              ? this.triggerNodes.length - 1
              : this.openIndex - 1;
          this.triggerNodes[index].focus();
          this.toggleExpand(index, true);
          this.controlledNodes[index]
            .querySelector("a, .ob-disclosure-nav__link")
            .focus();
        }
        break;
      case "ArrowUp":
        if (currentIndex > -1) {
          let prevIndex =
            currentIndex <= 0 ? nodeList.length - 1 : currentIndex - 1;
          nodeList[prevIndex].focus();
        }
        break;
      case "ArrowRight":
        if (this.triggerNodes.length > 0) {
          let index =
            this.openIndex >= this.triggerNodes.length - 1
              ? 0
              : this.openIndex + 1;
          this.triggerNodes[index].focus();
          this.toggleExpand(index, true);
          this.controlledNodes[index]
            .querySelector("a, .ob-disclosure-nav__link")
            .focus();
        }
        break;
      case "ArrowDown":
        if (currentIndex > -1) {
          let nextIndex =
            currentIndex >= nodeList.length - 1 ? 0 : currentIndex + 1;
          nodeList[nextIndex].focus();
        }
        break;
      case "Home":
        nodeList[0].focus();
        break;
      case "End":
        nodeList[nodeList.length - 1].focus();
        break;
    }
  }

  handleBlur(event) {
    let menuContainsFocus = this.contains(event.relatedTarget);
    if (!menuContainsFocus && this.openIndex !== null) {
      this.toggleExpand(this.openIndex, false);
    }
  }

  handleButtonKeyDown(event) {
    let targetButtonIndex = this.triggerNodes.indexOf(document.activeElement);

    // close on escape
    if (event.key === "Escape") {
      this.toggleExpand(this.openIndex, false);
    }

    // move focus into the related menu and opens it if necessary
    else if (this.useArrowKeys && event.key === "ArrowDown") {
      event.preventDefault();
      if (this.openIndex !== targetButtonIndex) {
        this.toggleExpand(targetButtonIndex, true);
      }
      this.controlledNodes[this.openIndex]
        .querySelector("a, .ob-disclosure-nav__link")
        .focus();
    }

    // handle arrow key navigation between top-level buttons, if set
    else if (this.useArrowKeys) {
      this.controlFocusByKey(event, this.triggerNodes, targetButtonIndex);
    }
  }

  handleButtonClick(event) {
    let button = event.currentTarget;
    let buttonIndex = this.triggerNodes.indexOf(button);
    let buttonExpanded = button.getAttribute("aria-expanded") === "true";
    this.toggleExpand(buttonIndex, !buttonExpanded);
  }

  handleMenuKeyDown(event) {
    if (this.openIndex === null) {
      return;
    }

    let menuLinks = Array.prototype.slice.call(
      this.controlledNodes[this.openIndex].querySelectorAll("a")
    );
    let currentIndex = menuLinks.indexOf(document.activeElement);

    // close on escape
    if (event.key === "Escape") {
      this.triggerNodes[this.openIndex].focus();
      this.toggleExpand(this.openIndex, false);
    }

    // handle arrow key navigation within menu links, if set
    else if (this.useArrowKeys) {
      this.controlFocusByKey(event, menuLinks, currentIndex);
    }
  }

  // switch on/off arrow key navigation
  updateKeyControls(useArrowKeys) {
    this.useArrowKeys = useArrowKeys;
  }
}

window.customElements.define("ob-disclosure-nav", DisclosureNav);
